import { Node } from "reactflow";
import { getConnectedInputs } from "../common/nodeDataFlow";

export const buildWorkflowPayload = (
  activeMeshFlow: any,
  workflowNodes: any[],
  connections: any,
  pluginId?: string,
) => {
  const pickValidPluginId = (...values: any[]): string => {
    for (const value of values) {
      if (typeof value !== "string") continue;
      const trimmed = value.trim();
      if (trimmed && trimmed !== "temp-plugin") return trimmed;
    }
    return "";
  };

  const pluginIdFromNodes = Array.isArray(workflowNodes)
    ? workflowNodes
        .map((node: any) =>
          pickValidPluginId(
            node?.plugin_id,
            node?.pluginId,
            node?.parameters?.plugin_id,
            node?.parameters?.pluginId,
            node?.data?.plugin_id,
            node?.data?.pluginId,
            node?.data?.configData?.pluginId,
            node?.data?.configData?.plugin_id,
          ),
        )
        .find((value: string) => value)
    : "";

  const resolvedPluginId = pickValidPluginId(
    pluginId,
    activeMeshFlow?.plugin_id,
    activeMeshFlow?.pluginId,
    sessionStorage.getItem("meshPluginId"),
    sessionStorage.getItem("editPluginId"),
    pluginIdFromNodes,
  );

  const isThreatLookupWorkflow =
    activeMeshFlow?.type_workflow === "threatlookup" ||
    activeMeshFlow?.type_workflow === "threat_lookup";

  const normalizedThreatlookupData = isThreatLookupWorkflow
    ? {
        search_type: activeMeshFlow?.threatlookup_data?.search_type || "",
        search_value: activeMeshFlow?.threatlookup_data?.search_value || "",
      }
    : activeMeshFlow?.threatlookup_data || {};

  const pin_data: Record<string, any[]> = {};
  if (workflowNodes && Array.isArray(workflowNodes)) {
    workflowNodes.forEach((node: any) => {
      const nodeUid = node.data?.uid || node.id;

      const prepareExecutionData = (data: any): any[] => {
        if (!data) return [];

        // If data is an array, we need to map each item to an INodeExecutionData object
        if (Array.isArray(data)) {
          // If already in backend format, return as is
          if (
            data.length > 0 &&
            typeof data[0] === "object" &&
            data[0] !== null &&
            "json_data" in data[0]
          ) {
            return data;
          }

          return data.map((item) => ({
            json_data:
              typeof item === "object" && item !== null && !Array.isArray(item)
                ? item
                : { data: item },
            binary: {},
            pairedItem: { item: 0 },
            error: null,
            logs: [],
          }));
        }

        // Single item
        return [
          {
            json_data:
              typeof data === "object" && data !== null && !Array.isArray(data)
                ? data
                : { data: data },
            binary: {},
            pairedItem: { item: 0 },
            error: null,
            logs: [],
          },
        ];
      };

      // ONLY pin if it was manually edited
      if (node.data?.manuallyEdited) {
        pin_data[nodeUid] = prepareExecutionData(
          node.data.response ||
            node.data.executionResult?.output_data ||
            node.data.executionResult,
        );
      }
    });
  }

  return {
    workflowData: {
      uid: activeMeshFlow.uid,
      title: activeMeshFlow.title,
      description: activeMeshFlow.description,
      type_workflow: activeMeshFlow.type_workflow,
      version: activeMeshFlow.version,
      cron_run_time:
        activeMeshFlow.cron_run_time ||
        (["save_log", "save_datalist", "itdr", "dspm"].includes(
          activeMeshFlow.type_workflow,
        )
          ? "5"
          : null),
      dependency_workflow: activeMeshFlow.dependency_workflow || [],
      threat_lookup_search_types:
        activeMeshFlow.threat_lookup_search_types || [],
      threatlookup_data: normalizedThreatlookupData,
      nodes: workflowNodes,
      connections: connections,
      pin_data,
      plugin_id: resolvedPluginId,
    },
  };
};

export const getNodeExecutionParameters = (
  node: Node,
  pluginId: string = "",
): { nodeParameters: any; parameters: any } => {
  const nodeType = node.type;
  let nodeParameters: any = {};

  // CRITICAL PATTERN: Check storedParameters FIRST, then fall back to parameters (backend format)
  if (node.data.storedParameters || node.data.parameters) {
    nodeParameters = node.data.storedParameters || node.data.parameters || {};

    if (nodeType === "authentication" || nodeType === "configurationNode") {
      const configData =
        node.data.configData || nodeParameters.configData || {};
      const resolvedPluginId =
        node.data.pluginId ||
        configData.pluginId ||
        configData.plugin_id ||
        nodeParameters.pluginId ||
        nodeParameters.plugin_id ||
        pluginId ||
        sessionStorage.getItem("editPluginId") ||
        sessionStorage.getItem("meshPluginId") ||
        "";

      nodeParameters = {
        ...nodeParameters,

        TabId: configData.tabId || configData.TabId || nodeParameters.TabId,

        Uid: configData.uid || configData.Uid || nodeParameters.Uid,

        pluginId: resolvedPluginId,

        configType:
          configData.configType || configData.type || nodeParameters.configType,

        configData,
      };
    }

    // Special validation for functionNode
    if (nodeType === "functionNode") {
      const action = nodeParameters.action || "threat_lookup";
      const mappingDataList = nodeParameters.mappingDataList || [];

      // For threat_lookup action, mappingDataList must have at least one item
      if (action === "threat_lookup" && mappingDataList.length === 0) {
        // Try to rebuild from multiple possible locations (support legacy field names)
        const rawMappings =
          nodeParameters.mappingThreatLookup ||
          node.data.mappingDataList ||
          node.data.mappingThreatLookup ||
          [];
        const rebuiltMappings = rawMappings
          .filter((m: any) => m.enabled !== false && m.key?.trim())
          .map((m: any) => ({
            key: m.key,
            values: m.values,
            is_custom: m.is_custom !== undefined ? m.is_custom : false,
            data_type: m.data_type || "string",
          }));

        // If still empty, throw error
        if (rebuiltMappings.length === 0) {
          throw new Error(
            `Function node "${node.data.title || node.id}" requires at least one field mapping for threat_lookup action`,
          );
        }

        // Update nodeParameters with rebuilt mappings
        nodeParameters = {
          ...nodeParameters,
          mappingDataList: rebuiltMappings,
        };
      }
    }
  } else {
    // Fallback: Build parameters manually based on node type
    switch (nodeType) {
      case "requestBlock":
        nodeParameters = {
          url: node.data.url || "",
          method: node.data.method || "GET",
          params: node.data.params || { type: "none", parameter: [] },
          headers: node.data.headers || [],
          auth: node.data.auth || {
            type: "none",
            token: "",
            username: "",
            password: "",
          },
          flag: 1,
          plugin_id: pluginId,
          ssl_verify: node.data.ssl_verify !== false,
          config: node.data.config || {},
          data: node.data.data || {},
          body_type: node.data.body_type || "none",
          body: node.data.body || null,
          raw_type: node.data.raw_type || null,
          filteration: node.data.filteration || {
            type: "none",
            default_cron_start_time: "none",
            start_key: "",
            end_key: "",
            custom_date_format: "",
          },
          pagination: node.data.pagination || { type: "none" },
        };
        break;

      case "editFieldsNode":
      case "editFields": {
        // Transform fields array to backend format
        const transformedFields = (node.data.fields || [])
          .filter((f: any) => f.enabled && f.fieldName?.trim())
          .map((f: any) => ({
            name: f.fieldName,
            type: f.fieldValueType || "stringValue",
            [f.fieldValueType || "stringValue"]: f.fieldValue,
          }));

        // Parse inputFieldsList to extract field names
        let parsedFieldsList = "";
        if (node.data.inputFieldsList && node.data.inputFieldsList.trim()) {
          const expressionMatches = node.data.inputFieldsList.match(
            /\{\{\$json\.([^}]+)\}\}/g,
          );
          if (expressionMatches) {
            const fieldNames = expressionMatches.map((match: string) =>
              match.replace(/\{\{\$json\./g, "").replace(/\}\}/g, ""),
            );
            parsedFieldsList = fieldNames.join(",");
          } else {
            parsedFieldsList = node.data.inputFieldsList;
          }
        }

        nodeParameters = {
          mode: node.data.mode || "manual",
          duplicateItem: false,
          duplicateCount: 0,
          includeOtherFields: node.data.includeOtherInputFields || false,
          include: node.data.inputFieldsMode || "all",
          fields: { values: transformedFields },
          options: {
            ignoreConversionErrors: false,
            dotNotation: node.data.supportDotNotation ?? true,
            includeBinary: false,
            continueOnFail: false,
          },
          ...(node.data.inputFieldsMode === "selected" &&
            parsedFieldsList && { selectedFields: parsedFieldsList }),
          ...(node.data.inputFieldsMode === "except" &&
            parsedFieldsList && { excludeFields: parsedFieldsList }),
        };
        break;
      }

      case "ifElseNode":
        nodeParameters = {
          conditions: {
            options: node.data.conditions?.options || {
              caseSensitive: true,
              leftValue: "",
              typeValidation: "strict",
              version: 3,
            },
            conditions: node.data.conditions?.conditions || [],
            combinator: node.data.conditions?.combinator || "and",
          },
          looseTypeValidation: node.data.looseTypeValidation || false,
          options: node.data.options || {},
        };
        break;

      case "switchNode": {
        nodeParameters = {
          mode: node.data.mode || "rules",
          rules: node.data.rules || { values: [] },
          looseTypeValidation: node.data.looseTypeValidation || true,
          options: node.data.options || {},
        };
        break;
      }

      case "splitOutNode": {
        const splitOutFields = (node.data.fieldsToSplitOut?.[0] || "")
          .split(",")
          .map((f: string) => f.trim())
          .filter((f: string) => f.length > 0);

        const nodeOptions = {
          disableDotNotation: node.data.options?.disableDotNotation ?? false,
          destinationFieldName:
            node.data.options?.destinationFieldName?.trim() ?? "",
        };

        // Map frontend enum values to backend expected values
        const mapIncludeToBackend = (frontendValue: string): string => {
          switch (frontendValue) {
            case "noOtherFields":
              return "specifiedFields"; // Only split field, no other fields
            case "allOtherFields":
              return "allFields"; // All fields including others
            case "selectedOtherFields":
              return "specifiedFields"; // Split field + selected fields
            default:
              return "allFields";
          }
        };

        const includeValue = node.data.include || "noOtherFields";
        nodeParameters = {
          fieldToSplitOut: splitOutFields.join(","),
          include: mapIncludeToBackend(includeValue),
          options: nodeOptions,
        };

        // Add fieldsToInclude if selectedOtherFields is chosen
        if (node.data.include === "selectedOtherFields") {
          if (node.data.fieldsToInclude?.[0]) {
            const includeFields = node.data.fieldsToInclude[0]
              .split(",")
              .map((f: string) => f.trim())
              .filter((f: string) => f.length > 0);
            nodeParameters.fieldsToInclude =
              includeFields.length > 0 ? includeFields.join(",") : "";
          } else {
            nodeParameters.fieldsToInclude = "";
          }
        } else if (node.data.include === "noOtherFields") {
          // For noOtherFields, set empty fieldsToInclude
          nodeParameters.fieldsToInclude = "";
        }
        break;
      }

      case "aggregateNode": {
        nodeParameters = node.data.storedParameters || {
          aggregate: node.data.aggregate || "aggregateAllItemData",
          fieldsToAggregate:
            node.data.aggregate === "aggregateIndividualFields" &&
            node.data.fieldsToAggregate
              ? {
                  fieldToAggregate: node.data.fieldsToAggregate.filter(
                    (f: any) => f.fieldToAggregate?.trim(),
                  ),
                }
              : null,
          destinationFieldName: node.data.destinationFieldName || "",
          include: node.data.include || "allFields",
          fieldsToInclude:
            node.data.include === "specifiedFields"
              ? node.data.fieldsToInclude || ""
              : "",
          fieldsToExclude:
            node.data.include === "allFieldsExcept"
              ? node.data.fieldsToExclude || ""
              : "",
          options: {
            keepMissing: node.data.options?.keepMissing ?? false,
            mergeLists: node.data.options?.mergeLists ?? false,
            disableDotNotation: node.data.options?.disableDotNotation ?? false,
          },
        };
        break;
      }

      case "mergeNode": {
        // Merge node has minimal parameters - backend will handle the merging logic
        nodeParameters = node.data.storedParameters || {};
        break;
      }

      case "savelogs": {
        const logMappings = node.data.logMappings || [];
        const mappingFields = logMappings
          .filter(
            (mapping: any) =>
              mapping.mappedLsField && mapping.mappedLsField.trim(),
          )
          .map((mapping: any, index: number) => ({
            fieldId: index + 1,
            rawFieldName:
              mapping.fieldPath?.replace("$json.", "") || mapping.fieldKey,
            type: "string",
            mappingLsField: mapping.mappedLsField,
            display_name: mapping.displayName || "",
            category: mapping.category || "",
            is_time: mapping.isTime || false,
          }));

        nodeParameters = {
          logUid: node.data.logUid || "",
          logTitile: node.data.logTitle || "",
          pluginId: pluginId,
          requestId: "",
          logCategory: node.data.logCategory || "",
          log_ls_product_type: node.data.log_ls_product_type || [],
          mappingFields: mappingFields,
          options: {
            batchSize: 100,
            continueOnError: false,
            validateDataTypes: true,
            disableDotNotation: false,
          },
        };
        break;
      }

      case "savedatalist": {
        const datalistMappings = node.data.datalistMappings || [];
        const datalistMappingFields = datalistMappings
          .filter(
            (mapping: any) =>
              mapping.mappedLsField &&
              mapping.mappedLsField.trim() &&
              mapping.columnType &&
              mapping.columnType.trim() &&
              mapping.dataType &&
              mapping.dataType.trim(),
          )
          .map((mapping: any, index: number) => ({
            fieldId: index + 1,
            rawFieldName:
              mapping.fieldPath?.replace("$json.", "") || mapping.fieldKey,
            type: "string",
            mappingLsField: mapping.mappedLsField,
            display_name: mapping.displayName || "",
            column_type: mapping.columnType,
            data_type: mapping.dataType,
          }));

        nodeParameters = {
          datalistUid: node.data.datalistUid || "",
          datalistTitle: node.data.datalistTitle || "",
          cronFrequency: node.data.cronFrequency || 0,
          cronType: node.data.cronType || "",
          groupBy: node.data.groupBy || "",
          pluginId: pluginId,
          requestId: "",
          mappingFields: datalistMappingFields,
          options: {
            batchSize: 100,
            continueOnError: false,
            validateDataTypes: true,
          },
        };
        break;
      }

      case "sortNode": {
        // Build parameters based on mode
        // Map UI mode back to backend mode
        const uiMode = node.data.mode || "simple";
        const backendMode =
          uiMode === "simple"
            ? "field"
            : uiMode === "code"
              ? "custom"
              : "random";

        const sortParameters: any = {
          mode: backendMode,
          nullsLast: node.data.nullsLast ?? true,
          options: {
            disableDotNotation: node.data.disableDotNotation ?? true,
          },
        };

        if (uiMode === "simple") {
          sortParameters.fields = (node.data.fields || [])
            .filter((f: any) => f.field?.trim())
            .map((f: any) => ({
              field: f.field,
              order: f.order || "asc",
            }));
        } else if (uiMode === "code") {
          sortParameters.order = node.data.order || "asc";
          sortParameters.customCode = node.data.customCode || "";
        }

        nodeParameters = sortParameters;
        break;
      }

      case "limitNode": {
        // Build limit parameters
        nodeParameters = {
          maxItems: node.data.maxItems || 10,
          keepItemsFrom: node.data.keepItemsFrom || "start",
        };
        break;
      }

      case "pythonCodeNode": {
        // Build Python code node parameters
        nodeParameters = {
          code: node.data.code || "result = {'processed': True}",
          mode: node.data.mode || "runOnceForAllItems",
          continueOnFail: node.data.continueOnFail || false,
          options: {
            python_env: node.data.pythonEnv || "python3",
          },
        };
        break;
      }

      case "startNode":
        // Start node doesn't need any parameters - it just outputs [{}]
        nodeParameters = {};
        break;

      case "authentication": {
        const configData = node.data.configData || {};
        const resolvedPluginId =
          node.data.pluginId ||
          configData.pluginId ||
          configData.plugin_id ||
          pluginId ||
          sessionStorage.getItem("editPluginId") ||
          sessionStorage.getItem("meshPluginId") ||
          "";

        nodeParameters = {
          ...nodeParameters,

          TabId: configData.tabId || configData.TabId || nodeParameters.TabId,

          Uid: configData.uid || configData.Uid || nodeParameters.Uid,

          pluginId: resolvedPluginId,

          configType:
            configData.configType ||
            configData.type ||
            nodeParameters.configType,

          configData,
        };

        break;
      }

      case "configurationNode": {
        const configData = node.data.configData || {};
        const resolvedPluginId =
          node.data.pluginId ||
          configData.pluginId ||
          configData.plugin_id ||
          pluginId ||
          sessionStorage.getItem("editPluginId") ||
          sessionStorage.getItem("meshPluginId") ||
          "";

        nodeParameters = {
          TabId: configData.tabId,
          Uid: configData.uid,
          pluginId: resolvedPluginId,
          configType: configData.configType,
          configData: {
            ...configData,
          },
        };
        break;
      }

      case "loopOverItemsNode": {
        // Loop Over Items node - extract batchSize and options
        nodeParameters = {
          mode: "splitInBatches",
          batchSize: node.data.batchSize || 1,
          options: {
            reset: node.data.reset ?? false,
            keepOnlySet: false,
          },
        };
        break;
      }

      case "functionNode": {
        // Function node - build threat lookup parameters
        // Support both new (mappingDataList) and legacy (mappingThreatLookup) field names
        const rawMappings =
          node.data.mappingDataList || node.data.mappingThreatLookup || [];

        const mappingDataList = rawMappings
          .filter((m: any) => m.enabled !== false && m.key?.trim())
          .map((m: any) => ({
            key: m.key,
            values: m.values,
            is_custom: m.is_custom !== undefined ? m.is_custom : false,
            data_type: m.data_type || "string",
          }));

        nodeParameters = {
          action: node.data.action || "threat_lookup",
          mappingDataList: mappingDataList,
        };
        break;
      }

      case "stopAndErrorNode": {
        nodeParameters = {
          errorType: node.data.errorType || "errorMessage",
          errorMessage:
            node.data.errorMessage ||
            "HTTP request failed with status code 500",
        };
        break;
      }

      case "connectFlow": {
        // Connect Flow node - workflow composition
        nodeParameters = {
          workflow_id: node.data.workflow_id || "",
          mode: node.data.mode || "once",
        };
        break;
      }

      case "exportedMeshflow": {
        // Exported Meshflow node - no parameters needed (executes embedded workflow)
        nodeParameters = {};
        break;
      }

      default:
        // Unknown node type - return node.data as fallback
        console.warn(
          `Unknown node type "${nodeType}" in getNodeExecutionParameters, using node.data as fallback`,
        );
        nodeParameters = node.data;
    }
  }

  // Filter out UI-only fields that shouldn't be sent to backend
  const { ...cleanParameters } = nodeParameters;

  // Return both raw parameters and cleaned parameters
  return {
    nodeParameters,
    parameters: cleanParameters,
  };
};

// Find all downstream nodes of a given node by traversing edges
export const findDownstreamNodes = (
  nodeId: string,
  edges: any[],
): Set<string> => {
  const downstream = new Set<string>();
  const queue = [nodeId];
  const visited = new Set<string>();

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    if (visited.has(currentId)) continue;
    visited.add(currentId);

    // Find all edges where current node is the source
    const outgoingEdges = edges.filter((e) => e.source === currentId);

    outgoingEdges.forEach((edge) => {
      downstream.add(edge.target);
      queue.push(edge.target);
    });
  }

  return downstream;
};

// Process node execution results, update node states, and identify errors
export const handleNodeExecutionResults = (
  reactFlowInstance: any,
  nodeResults: any[],
  edges: any[],
): {
  hasErrors: boolean;
  errorMessage: string;
  errorNodeTitle: string;
  failedNodeIds: Set<string>;
  downstreamOfFailedNodes: Set<string>;
} => {
  // Build a map of node results for quick lookup
  const resultsMap = new Map<string, any>();
  nodeResults.forEach((result: any) => {
    resultsMap.set(result.uid, result);
  });

  // Find failed nodes and their downstream nodes
  const failedNodeIds = new Set<string>();
  const downstreamOfFailedNodes = new Set<string>();
  let firstErrorMessage = "";
  let firstErrorNodeTitle = "";

  nodeResults.forEach((result: any) => {
    // Backend can return either "error" or "failed" status
    if (
      result.status === "error" ||
      result.status === "failed" ||
      result.status === "failure"
    ) {
      failedNodeIds.add(result.uid);

      // Capture first error message for toast
      if (!firstErrorMessage && result.error) {
        const errorArray = Array.isArray(result.error)
          ? result.error
          : [result.error];
        const firstError = errorArray[0];
        firstErrorMessage =
          firstError?.message || firstError || "Unknown error";
        firstErrorNodeTitle = result.title || "Unknown Node";
      }

      // Find all downstream nodes
      const downstream = findDownstreamNodes(result.uid, edges);
      downstream.forEach((nodeId) => downstreamOfFailedNodes.add(nodeId));
    }
  });

  const hasErrors = failedNodeIds.size > 0;

  // Update all nodes with their execution results AND inputData in a single update
  reactFlowInstance.setNodes((nodes: Node[]) =>
    nodes.map((node: Node) => {
      // Match results using the same UID logic used when building the workflow
      // Backend uses node.data.uid if present, otherwise node.id
      const nodeUid = node.data.uid || node.id;
      const result = resultsMap.get(nodeUid);

      // Calculate inputData from connected nodes
      const connectedInputs = node.data.configData
        ? [] // Skip input calculation for config nodes
        : getConnectedInputs(node.id, edges, nodes);

      // Handle configuration/authentication nodes
      if (node.data.configData) {
        if (result) {
          // Backend may return "error", "failed", "success", or "completed"
          // Normalize to either "success" or "failed" for UI consistency
          let status =
            result.status === "success" || result.status === "completed"
              ? "success"
              : "failed";

          // If this config node failed or is downstream of a failed node, mark as failed
          if (
            failedNodeIds.has(nodeUid) ||
            downstreamOfFailedNodes.has(nodeUid)
          ) {
            status = "failed";
          }

          return {
            ...node,
            data: {
              ...node.data,
              executionStatus: status,
              executionResult: result,
              error: result.error,
              lastUpdated: Date.now(),
            },
          };
        } else if (downstreamOfFailedNodes.has(nodeUid)) {
          // No result but downstream of failed node - mark as failed
          return {
            ...node,
            data: {
              ...node.data,
              executionStatus: "failed",
              error: "Not executed due to upstream failure",
            },
          };
        }
      }

      // Handle regular workflow nodes
      if (result) {
        // Backend may return "error", "failed", "success", or "completed"
        // Normalize to either "success" or "failed" for UI consistency
        let status =
          result.status === "success" || result.status === "completed"
            ? "success"
            : "failed";

        // If this node failed or is downstream of a failed node, mark as failed
        if (
          failedNodeIds.has(nodeUid) ||
          downstreamOfFailedNodes.has(nodeUid)
        ) {
          status = "failed";
        }

        // Unwrap json_data from the output
        const unwrappedData = unwrapJsonData(result.output_data);
        const freshData = JSON.parse(JSON.stringify(unwrappedData));

        return {
          ...node,
          data: {
            ...node.data,
            executionStatus: status,
            executionResult: result,
            response: freshData,
            outputData: freshData,
            executionTime: result.execution_time,
            error: result.error,
            inputData:
              connectedInputs.length > 0
                ? connectedInputs
                : node.data.inputData,
            lastUpdated: Date.now(),
          },
        };
      } else if (
        node.data.executionStatus === "pending" ||
        downstreamOfFailedNodes.has(nodeUid)
      ) {
        // Node was marked as pending but didn't execute
        // OR is downstream of a failed node - mark as failed
        return {
          ...node,
          data: {
            ...node.data,
            executionStatus: downstreamOfFailedNodes.has(nodeUid)
              ? "failed"
              : undefined,
            executionResult: undefined,
            inputData:
              connectedInputs.length > 0
                ? connectedInputs
                : node.data.inputData,
            error: downstreamOfFailedNodes.has(nodeUid)
              ? "Not executed due to upstream failure"
              : undefined,
          },
        };
      }

      // No result for this node, but update inputData if available
      if (connectedInputs.length > 0) {
        return {
          ...node,
          data: {
            ...node.data,
            inputData: connectedInputs,
          },
        };
      }

      return node;
    }),
  );

  return {
    hasErrors,
    errorMessage: firstErrorMessage,
    errorNodeTitle: firstErrorNodeTitle,
    failedNodeIds,
    downstreamOfFailedNodes,
  };
};

// Unwrap json_data from node execution results, handling both single objects and arrays
export const unwrapJsonData = (data: any): any => {
  if (!data) return data;

  if (Array.isArray(data)) {
    return data.map((item: any) => {
      if (item && typeof item === "object" && "json_data" in item) {
        return item.json_data;
      }
      return item;
    });
  } else if (
    data &&
    typeof data === "object" &&
    "json_data" in data &&
    Object.keys(data).length <= 4
  ) {
    return data.json_data;
  }

  return data;
};
